import React, { FC, memo, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { useRoutesHelper } from '../../../helpers/routesHelper';
import { useSelector } from '../../../configureStore';
import { useStartDistributeMutation } from '../../../core/api/eventsSlice';
import { useCreateDistributorByEmailMutation } from '../../../core/api/accountSlice';
import { PagesRoutes } from '../../../store/routes';
import { Button, PageFooter, TextInput } from '../../../components/UI';
import { EventDetailsType } from '../../../core/models/eventDetails';
import { UserEventType } from '../../../core/models/events';

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

type EventDetailsFooterType = {
  event: EventDetailsType;
  isInitializing: boolean;
};

export const EventDetailsFooter: FC<EventDetailsFooterType> = memo(({ event, isInitializing }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { getBookEventRoute, getSellEventRoute, createReferralLink } = useRoutesHelper();

  const isAuthorized = useSelector((state) => state.app.isAuthorized);
  const userEvents = useSelector((state) => state.userEvents);

  const [startDistributeRequest, { isLoading: isLoadingStartDistribute }] = useStartDistributeMutation();
  const [createDistributor, { isLoading: isLoadingCreateDistributor }] = useCreateDistributorByEmailMutation();

  const [showGetLink, setShowGetLink] = useState(false);
  const [email, setEmail] = useState('');
  const [referralSlug, setReferralSlug] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  const referralUrl = useMemo(() => (referralSlug ? createReferralLink(referralSlug) : ''), [createReferralLink, referralSlug]);
  const hasLink = !!referralUrl;
  const canCreate = isValidEmail(email) && !isLoadingCreateDistributor && !hasLink;

  const copyToClipboard = async (text: string) => {
    if (navigator?.clipboard?.writeText) {
      try { await navigator.clipboard.writeText(text); return true; } catch { /* fall through */ }
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch { return false; }
  };

  const handleGetLink = async () => {
    if (hasLink) {
      const ok = await copyToClipboard(referralUrl);
      if (ok) { setIsCopied(true); window.setTimeout(() => setIsCopied(false), 5000); }
      return;
    }
    if (!canCreate) return;
    try {
      const res = await createDistributor({ email: email.trim() }).unwrap();
      if (res?.referralSlug) setReferralSlug(res.referralSlug);
    } catch { /* user can retry */ }
  };

  if (!event) {
    return null;
  }
  const isDomainsEmpty = isEmpty(event.domains);

  const isEventDetailsDistributed = (event: EventDetailsType, userEvents?: UserEventType[]): boolean =>
    !!event && userEvents.some((e) => e.title === event.title);

  const isDistributed = isEventDetailsDistributed(event, userEvents);
  const sellTicketButtonTitle = isDistributed ? t('Event.SellTicket') : t('Event.BecomeDistributor');
  const showDistributionButton = isAuthorized && !isInitializing && event.isDistributionAvailable;

  const getBookTicketsButtonTitle = () => {
    if (!event.isTicketsAvailable) {
      return t('Event.NoTicketsAvailable');
    }
    if (!event.isRegionAvailable) {
      return t('Event.NotAvailableForYourRegion');
    }
    if (isDomainsEmpty) {
      return t('Event.TemporarilyUnavailable');
    }

    return t('Event.BuyTicket');
  };

  const bookTicketsButtonTitle = getBookTicketsButtonTitle();

  const handleOnBookClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
    }
    navigate(getBookEventRoute(event.slug));
  };

  const handleOnDistributeClick = (e: MouseEvent<HTMLButtonElement>, event: EventDetailsType) => {
    if (e) {
      e.preventDefault();
    }

    if (isEventDetailsDistributed(event, userEvents)) {
      navigate(getSellEventRoute(event.slug));
      return;
    }

    (async () => await startDistributeRequest(event.id))().then(() => {
      navigate(PagesRoutes.UserEvents);
    });
  };

  return (
    <PageFooter>
      <AnimatePresence initial={false} mode="wait">
        {showGetLink ? (
          <GetLinkForm
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <AnimatePresence mode="wait">
              {hasLink ? (
                <motion.div
                  key="link"
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <GetLinkDisplayUrl>{referralUrl}</GetLinkDisplayUrl>
                </motion.div>
              ) : (
                <motion.div
                  key="input"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <GetLinkInput
                    type="email"
                    value={email}
                    placeholder="you@example.com"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleGetLink(); }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <Button
              disabled={hasLink ? false : !canCreate}
              isLoading={isLoadingCreateDistributor}
              onClick={() => void handleGetLink()}
            >
              {isLoadingCreateDistributor
                ? 'Please wait...'
                : hasLink
                  ? isCopied
                    ? t('Share.Copied')
                    : t('Share.CopyLink')
                  : 'SHARE LINK - EARN MONEY'}
            </Button>
          </GetLinkForm>
        ) : (
          <FooterCtaWrap
            key="get-link-btn"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <FooterOutlinePulseButton outline onClick={() => setShowGetLink(true)}>
              SHARE LINK - EARN MONEY
            </FooterOutlinePulseButton>
          </FooterCtaWrap>
        )}
      </AnimatePresence>
      <Button disabled={!event.isTicketsAvailable || isDomainsEmpty} onClick={(e) => handleOnBookClick(e)}>
        {bookTicketsButtonTitle}
      </Button>
      {showDistributionButton && (
        <Button
          disabled={isDomainsEmpty || isLoadingStartDistribute || !event.isTicketsAvailable}
          isLoading={isLoadingStartDistribute}
          onClick={(e) => handleOnDistributeClick(e, event)}
        >
          {sellTicketButtonTitle}
        </Button>
      )}
    </PageFooter>
  );
});

const GetLinkForm = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const GetLinkInput = styled(TextInput as any)`
  padding: 15px 20px;
  font-size: 13px;
`;

const GetLinkDisplayUrl = styled.div`
  width: 100%;
  padding: 15px 20px;
  border: 1px solid ${(props) => props.theme.colors.accent};
  border-radius: 24px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.accent};
  word-break: break-all;
  text-align: center;
  user-select: all;
`;

const FooterCtaWrap = styled(motion.div)`
  width: 100%;
  margin-bottom: 20px;
`;

const FooterOutlinePulseButton = styled(Button)`
  && {
    border-radius: 999px;
    animation: outlinePulse 1.5s ease-in-out infinite;
  }

  @keyframes outlinePulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.0);
    }
    50% {
      box-shadow: 0 0 0 10px rgba(255, 255, 255, 0.08);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
